import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartyBalance } from '@prody/shared';
import { AGEING_BUCKETS } from '@prody/shared';
import {
  Button, DataTable, EmptyState, IconPlus, ListPane, MoneyText, PageHeader,
  Segmented, Stamp, Toolbar, Tooltip, Workbench, type Column,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useBalances } from '~/platform/data/queries';
import './payments.css';

/* ==================================================================
   Debt, both ways.

   "Who owes us" and "who do we owe" are the same question with the
   sign flipped, so they are one screen. The ageing profile is drawn
   as a stacked bar per row rather than five numeric columns: what an
   accountant needs at a glance is the *shape* of a debt — €4k that is
   all current is a different conversation from €4k that is all 90+ —
   and five right-aligned figures do not communicate shape.
   ================================================================== */

export default function PaymentsPage() {
  const { t } = useTranslation(['payments', 'common']);
  const { business, format } = useBusiness();
  const [direction, setDirection] = useState<'receivable' | 'payable'>('receivable');

  const { data: balances = [], isLoading } = useBalances(business.id, direction);

  const total = useMemo(
    () => balances.reduce((sum, b) => sum + b.outstanding.minor, 0),
    [balances],
  );
  const overdue = useMemo(
    () => balances.reduce((sum, b) => sum + b.overdue.minor, 0),
    [balances],
  );

  const columns = useMemo<Column<PartyBalance>[]>(
    () => [
      {
        key: 'party',
        header: t('common:labels.customer'),
        width: 'minmax(170px, 1.6fr)',
        sortable: true,
        sortValue: (b) => b.partyName,
        cell: (b) => <span className="truncate">{b.partyName}</span>,
      },
      {
        key: 'invoices',
        header: t('invoices:title'),
        align: 'num',
        width: '70px',
        hideBelow: 'sm',
        cell: (b) => <span className="num">{b.invoiceCount}</span>,
      },
      {
        key: 'ageing',
        header: t('ageing'),
        width: 'minmax(180px, 1.4fr)',
        hideBelow: 'md',
        cell: (b) => <AgeingBar balance={b} />,
      },
      {
        key: 'oldest',
        header: t('common:labels.date'),
        align: 'num',
        width: '92px',
        hideBelow: 'lg',
        sortable: true,
        sortValue: (b) => b.oldestDays,
        cell: (b) =>
          b.oldestDays > 0 ? (
            <span className="num" data-tone="alert" style={{ color: 'var(--alert)' }}>
              {b.oldestDays} d
            </span>
          ) : (
            <span className="faint">—</span>
          ),
      },
      {
        key: 'overdue',
        header: `${t('common:labels.status')} (${format.currencySymbol()})`,
        align: 'num',
        width: '116px',
        sortable: true,
        sortValue: (b) => b.overdue.minor,
        cell: (b) =>
          b.overdue.minor > 0 ? <MoneyText value={b.overdue} bare /> : <span className="faint">—</span>,
      },
      {
        key: 'outstanding',
        header: `${t('common:labels.total')} (${format.currencySymbol()})`,
        align: 'num',
        width: '124px',
        sortable: true,
        sortValue: (b) => b.outstanding.minor,
        cell: (b) => <MoneyText value={b.outstanding} bare size="md" />,
      },
      {
        key: 'limit',
        header: t('contacts:creditLimit'),
        width: '112px',
        hideBelow: 'lg',
        cell: (b) => {
          if (b.creditLimitMinor === null) return <span className="faint">—</span>;
          const over = b.outstanding.minor > b.creditLimitMinor;
          return over ? (
            <Stamp tone="alert" weight="solid">
              {t('contacts:overLimit')}
            </Stamp>
          ) : (
            <span className="num dim">
              {format.money({ minor: b.creditLimitMinor, currency: b.outstanding.currency }, { bare: true, compact: true })}
            </span>
          );
        },
      },
    ],
    [t, format],
  );

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        actions={
          <Button variant="primary" icon={<IconPlus />}>
            {t('recordPayment')}
          </Button>
        }
      />

      <Toolbar
        trailing={
          <div className="pd-debt__totals">
            <span>
              <span className="stencil">{t('common:labels.total')}</span>
              <MoneyText value={{ minor: total, currency: format.currency }} />
            </span>
            <span>
              <span className="stencil">{t('buckets.d1_30')}+</span>
              <MoneyText value={{ minor: overdue, currency: format.currency }} tone />
            </span>
          </div>
        }
      >
        <Segmented<'receivable' | 'payable'>
          label={t('title')}
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'receivable', label: t('receivable') },
            { value: 'payable', label: t('payable') },
          ]}
        />
      </Toolbar>

      <ListPane>
        <DataTable<PartyBalance>
          rows={balances}
          columns={columns}
          rowKey={(b) => b.partyId}
          rowMeta={(b) => ({
            handlingClass: direction === 'receivable' ? 'ambient' : 'neutral',
            ...(b.oldestDays > 60 ? { tone: 'alert' as const } : b.oldestDays > 0 ? { tone: 'warn' as const } : {}),
          })}
          loading={isLoading}
          caption={t('title')}
          empty={<EmptyState title={t('empty')} body={t('emptyBody')} />}
        />
      </ListPane>
    </Workbench>
  );
}

/**
 * A stacked ageing bar. Segments carry the same tone ramp used
 * everywhere else — current is neutral, and it reddens with age — so
 * the meaning is learned once.
 */
function AgeingBar({ balance }: { balance: PartyBalance }) {
  const { t } = useTranslation(['payments']);
  const { format } = useBusiness();
  const total = balance.outstanding.minor || 1;

  return (
    <span className="pd-ageing">
      {AGEING_BUCKETS.map((bucket) => {
        const value = balance.buckets[bucket.key];
        if (value <= 0) return null;
        return (
          <Tooltip
            key={bucket.key}
            content={`${t(`buckets.${bucket.key}`)} · ${format.money({ minor: value, currency: balance.outstanding.currency })}`}
          >
            <span
              className="pd-ageing__seg"
              data-bucket={bucket.key}
              style={{ flexGrow: value / total }}
            />
          </Tooltip>
        );
      })}
    </span>
  );
}
